import * as React from 'react';
import {
  AboutModal,
  Button,
  Content,
  Divider
} from '@patternfly/react-core';
import casLight from '@app/bgimages/cas-light.svg';
import casDark from '@app/bgimages/cas-dark.svg';
import casBackgroun from '@app/bgimages/cas-background.svg';
import { ddMMyyyyHHmmTzFormat } from '@app/utils/functions';
import { APP_VERSION, BUILD_DATE, COMMIT_HASH } from '@app/utils/config';
import { useApiVersion } from '@app/hooks/useApiVersion';
import { WhatsNewModal } from '@app/components/WhatsNew';
import { SendNotificationModal } from '@app/components/SendNotificationModal';
import { PARAMETER, useParameterStringList } from '@app/store/parameterStore';
import { getUserRoles } from '@app/utils/keycloak';

export interface VersionInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  theme?: 'light' | 'dark';
}

/**
 * The way in to the announcement form: this many clicks on the logo, each within
 * LOGO_CLICK_WINDOW_MS of the last.
 *
 * Hidden rather than put in the navigation because sending a message to everybody is rare, and a
 * menu item for it is a menu item somebody clicks to see what it does.
 */
const LOGO_CLICKS_TO_OPEN = 4;
const LOGO_CLICK_WINDOW_MS = 2000;

/**
 * Identifies the logo among everything else in the dialog.
 *
 * AboutModal renders the brand image itself and gives nothing to hang a handler on, so the click is
 * caught on the way past and matched here. Our own `brandImageAlt` rather than PatternFly's internal
 * class name, so a change to their markup cannot quietly stop this from matching.
 */
const BRAND_ALT = 'Application Logo';

/**
 * The About dialog. Controlled, because it is opened from two places: the version footer in
 * the sidebar and the About item in the user menu.
 */
export const VersionInfoModal: React.FC<VersionInfoModalProps> = ({ isOpen, onClose, theme = 'light' }) => {
  const [isWhatsNewOpen, setIsWhatsNewOpen] = React.useState(false);
  const [isSendNotificationOpen, setIsSendNotificationOpen] = React.useState(false);
  const apiVersion = useApiVersion();

  // The same roles the service checks the announcement against, read from the service so that
  // widening who may announce does not need a release. The fallback is what the parameter itself
  // defaults to, for the moment before the parameters have loaded.
  const announceRoles = useParameterStringList(PARAMETER.NOTIFICATION_ANNOUNCE_ROLES, ['admin']);
  const canAnnounce = React.useMemo(
    () => getUserRoles().some((role) => announceRoles.includes(role)),
    [announceRoles]
  );

  // About gives way to the release notes rather than sitting behind them: two open modals
  // both claim the screen reader by hiding every other child of <body>, and whichever
  // renders last wins, so the one in front is as likely to be the hidden one.
  const showWhatsNew = () => {
    onClose();
    setIsWhatsNewOpen(true);
  };

  const logoClicks = React.useRef(0);
  const clickTimer = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  React.useEffect(() => () => clearTimeout(clickTimer.current), []);

  /**
   * Counts clicks on the logo and opens the announcement form on the fourth.
   *
   * Reached from a wrapper around AboutModal rather than from the image, which PatternFly owns.
   * A modal is rendered through a portal, so this element is not a DOM ancestor of the image - but
   * React propagates events up the element tree it rendered rather than the DOM tree, so the click
   * arrives here all the same.
   *
   * Nothing happens for anybody who may not announce, so the only person who can stumble into this
   * is one the service would have let through anyway.
   */
  const onAboutClick = (event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    if (!canAnnounce || target.tagName !== 'IMG' || target.getAttribute('alt') !== BRAND_ALT) return;

    clearTimeout(clickTimer.current);
    logoClicks.current += 1;

    if (logoClicks.current >= LOGO_CLICKS_TO_OPEN) {
      logoClicks.current = 0;
      onClose(); // gives way, for the same reason as What's new above
      setIsSendNotificationOpen(true);
      return;
    }

    // Four deliberate clicks, not four clicks whenever. Without this every stray click on the logo
    // over the life of the dialog counts towards the next one.
    clickTimer.current = setTimeout(() => { logoClicks.current = 0; }, LOGO_CLICK_WINDOW_MS);
  };

  return (
    <div onClick={onAboutClick}>
      <AboutModal
        isOpen={isOpen}
        onClose={onClose}
        trademark="Copyright © 2026 NIIT Ltd."
        brandImageSrc={theme === 'light' ? casLight : casDark}
        brandImageAlt="Application Logo"
        productName="Class Allocation System"
        backgroundImageSrc={casBackgroun}
      >
        <Content>
          <dl>
            <dt>App Version</dt>
            <dd>{APP_VERSION}</dd>

            <dt>Commit Hash</dt>
            <dd style={{ fontFamily: 'monospace' }}>{COMMIT_HASH}</dd>

            <dt>Build Date</dt>
            <dd>{BUILD_DATE ? ddMMyyyyHHmmTzFormat(BUILD_DATE) : 'n/a'}</dd>

            <dt>API Version</dt>
            <dd>{apiVersion ?? 'n/a'}</dd>

            <dt>Browser</dt>
            <dd>{navigator.userAgent}</dd>

          </dl>

          <Button variant="link" isInline onClick={showWhatsNew}>
            What&apos;s new
          </Button>
        </Content>
      </AboutModal>

      <WhatsNewModal isOpen={isWhatsNewOpen} onClose={() => setIsWhatsNewOpen(false)} />

      {/* Hidden: opened only by the four clicks above, and only for somebody who may announce. */}
      <SendNotificationModal
        isOpen={isSendNotificationOpen}
        onClose={() => setIsSendNotificationOpen(false)}
      />
    </div>
  );
};

export interface VersionFooterProps {
  /** Opens the About dialog, which the layout owns so the user menu can open it too. */
  onClick: () => void;
}

/** The subtle version/build stamp at the foot of the sidebar. */
export const VersionInfo: React.FC<VersionFooterProps> = ({ onClick }) => {
  const apiVersion = useApiVersion();

  const truncateHash = (hash: string) => {
    if (hash.length <= 12) return hash;
    return `${hash.substring(0, 12)}...`;
  }

  return (
    <div
      onClick={onClick}
      style={{
        padding: '16px',
        fontSize: '12px',
        color: 'var(--pf-v6-global--Color--dark-200)', // Subtle grey
        cursor: 'pointer',
        transition: 'color 0.2s'
      }}
      onMouseEnter={(e) => e.currentTarget.style.color = 'var(--pf-v6-global--link--Color)'}
      onMouseLeave={(e) => e.currentTarget.style.color = 'var(--pf-v6-global--Color--dark-200)'}
    >
      <Divider style={{ marginBottom: '16px' }} />
      <div style={{ fontWeight: 'bold' }}>Version {APP_VERSION}</div>
      <div style={{ fontFamily: 'monospace', opacity: 0.8 }}>
        {/* Above the build lines, not below them: the feedback balloon floats over the bottom
            of the sidebar, so the last line of this block is the one it hides. Shown only once
            it is known — an "API n/a" line in a footer this small is noise, and the About
            dialog is where someone goes when they need the answer. */}
        {apiVersion && <>API {apiVersion}<br/></>}
        Build <span style={{ fontFamily: 'monospace' }}>{truncateHash(COMMIT_HASH)}</span><br/>
        on {BUILD_DATE ? ddMMyyyyHHmmTzFormat(BUILD_DATE) : 'n/a'}
      </div>
    </div>
  );
};
